import { getAcademicYearByDate } from './getAcademicYearByDate';
import { isDateValid } from './isDateValid';

const PERIOD_REGEXP_LIST = [/(?<startYear>\d{4})\s*[-/]\s*(?<endYear>\d{4})/];

type YearsRangeMatch = {
  startYear: string;
  endYear: string;
};
const isYearsRangeMatch = (match: RegExpMatchArray | null): match is RegExpMatchArray & { groups: YearsRangeMatch } => {
  return match !== null && match.groups !== undefined && 'startYear' in match.groups && 'endYear' in match.groups;
};

const extractDate = (obj: any, rule: DateFieldRule): string[] => {
  const value = obj[rule.field];

  if (!value) {
    return [];
  }

  const date = new Date(value);

  if (!isDateValid(date)) {
    return [];
  }

  return [getAcademicYearByDate(date)];
};

const extractAcademicYearString = (obj: any, rule: PeriodFieldRule): string[] => {
  const value = obj[rule.field];

  if (typeof value !== 'string') {
    return [];
  }

  for (const regexp of PERIOD_REGEXP_LIST) {
    const match = value.match(regexp);

    if (!isYearsRangeMatch(match)) {
      continue;
    }

    return [`${match.groups.startYear}-${match.groups.endYear}`];
  }

  return [];
};

const extractRange = (obj: any, rule: DateRangeRule): string[] => {
  const startDate = new Date(obj[rule.startField]);
  const endDate = new Date(obj[rule.endField]);

  if (!isDateValid(startDate) || !isDateValid(endDate)) {
    return [];
  }

  const startYear = Number(getAcademicYearByDate(startDate).split('-')[0]);
  const endYear = Number(getAcademicYearByDate(endDate).split('-')[1]);

  const result: string[] = [];

  for (let year = startYear; year < endYear; year++) {
    result.push(`${year}-${year + 1}`);
  }

  return result;
};

export const ExtractRuleType = {
  Date: 'date',
  Range: 'range',
  AcademicYearString: 'academicYearString',
} as const;

type DateFieldRule = {
  type: typeof ExtractRuleType.Date;
  field: string;
};

type DateRangeRule = {
  type: typeof ExtractRuleType.Range;
  startField: string;
  endField: string;
};

type PeriodFieldRule = {
  type: typeof ExtractRuleType.AcademicYearString;
  field: string;
};

type ExtractRule = DateFieldRule | DateRangeRule | PeriodFieldRule;

export const extractDatesFromNestedItems = (items: any[] | any | null | undefined, rules: ExtractRule[]): string[] => {
  const periods = new Set<string>();

  function walk(obj: unknown) {
    if (!obj || typeof obj !== 'object') {
      return;
    }

    // Если obj — массив, проходим по элементам
    if (Array.isArray(obj)) {
      obj.forEach(walk);
      return;
    }

    for (const rule of rules) {
      let years: string[] = [];
      switch (rule.type) {
        case ExtractRuleType.Date:
          years = extractDate(obj, rule);
          break;

        case ExtractRuleType.Range:
          years = extractRange(obj, rule);
          break;

        case ExtractRuleType.AcademicYearString:
          years = extractAcademicYearString(obj, rule);
          break;
      }

      years.forEach((year) => periods.add(year));
    }

    Object.values(obj).forEach((value) => {
      if (typeof value === 'object') {
        walk(value);
      }
    });
  }

  walk(items);
  return Array.from(periods);
};
